import React from "react";
import { Button, Typography } from "../../components/common";
import { Download, Eye, Calendar, Home } from "lucide-react";
import { useCertificatesHook } from "./useCertificatesHook";
import { formatDate } from "../../utils/utils";
import { Certificate } from "../../types/newtypes";

const generatingCopy =
  "Certificate is generating, please check back in a few minutes";
const failedCopy =
  "Your certificate is taking longer than expected — we're retrying automatically. If it doesn't appear shortly, contact support.";

const getPdfStatusCopy = (certificate: Certificate) => {
  if (certificate.download_uri) {return null;}
  return certificate.pdf_status === "failed" ? failedCopy : generatingCopy;
};

const Certificates: React.FC = () => {
  const {
    certificates,
    loading,
    error,
    handleViewCertificate,
    handleDownloadCertificate,
    navigateHome,
    navigateToCourses,
  } = useCertificatesHook();

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-background p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <Typography variant="p" className="mt-4 text-textPrimary-grey">
          Loading your certificates...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-16">
            <Typography
              variant="h2"
              weight="bold"
              className="mb-3 text-textPrimary"
            >
              Unable to Load Certificates ☹️
            </Typography>
            <Typography
              variant="p"
              className="text-textPrimary-grey max-w-md mx-auto mb-4 leading-relaxed"
            >
              We encountered an issue while fetching your certificates. This
              might be due to a temporary network issue or server problem.
            </Typography>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="filled"
                onClick={navigateHome}
                startIcon={<Home className="w-4 h-4" />}
              >
                Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center justify-center gap-4 mb-8">
          <div className="flex gap-2 justify-start items-center mx-auto">
            <Typography
              variant="h1"
              weight="bold"
              className="text-start text-textPrimary"
            >
              🎓 Your Certificates
            </Typography>
          </div>

          <div className="text-start">
            <Typography
              variant="p"
              className="text-lg text-textPrimary-grey mx-auto mt-1"
            >
              Celebrate your achievements! View and download all the
              certificates you've earned through your learning journey.
            </Typography>
          </div>
        </div>

        {/* Certificates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {certificates.map((certificate) => {
            const pdfStatusCopy = getPdfStatusCopy(certificate);
            const downloadDisabled = !certificate.download_uri;
            const card = (
              <div
                key={certificate.id}
                data-testid="cert-card"
                data-certificate-id={certificate.id}
                className="certificate-card bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-4"
              >
                {/* Certificate Header with Gradient */}
                <div className="relative overflow-hidden">
                  <div className="relative z-10">
                    <Typography
                      variant="h5"
                      weight="bold"
                      className="line-clamp-2 leading-tight"
                    >
                      {certificate?.course_name}
                    </Typography>
                  </div>
                </div>

                {/* Certificate Content */}
                <div className="mt-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div className="flex items-center text-gray-500">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <Typography variant="small" className="text-gray-600">
                        Issued on {formatDate(certificate?.issued_at || "")}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-3">
                      <Typography
                        variant="small"
                        weight="medium"
                        className="!text-slate-600"
                      >
                        Score :
                      </Typography>
                      <Typography
                        variant="small"
                        weight="bold"
                        className="!text-slate-400"
                      >
                        {certificate?.score || 100}%
                      </Typography>
                    </div>
                  </div>

                  {/* Action Buttons - Icon Only */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewCertificate(certificate)}
                      className="flex-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 p-3 rounded-xl transition-all duration-200 flex items-center justify-center group/btn"
                      title="View Certificate"
                    >
                      <Eye className="w-5 h-5 group-hover/btn:scale-110 transition-transform duration-200" />
                    </button>
                    <button
                      onClick={() => handleDownloadCertificate(certificate)}
                      className="flex-1 bg-green-50 hover:bg-green-100 text-green-600 p-3 rounded-xl transition-all duration-200 flex items-center justify-center group/btn disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-50"
                      title={pdfStatusCopy || "Download Certificate"}
                      aria-label="Download certificate"
                      data-testid="cert-download-btn"
                      disabled={downloadDisabled}
                    >
                      <Download className="w-5 h-5 group-hover/btn:scale-110 transition-transform duration-200" />
                    </button>
                  </div>
                  {pdfStatusCopy && (
                    <Typography
                      variant="small"
                      className="mt-3 block text-slate-600 leading-relaxed"
                    >
                      {pdfStatusCopy}
                    </Typography>
                  )}
                </div>
              </div>
            );
            return card;
          })}
        </div>

        {/* Empty State */}
        {certificates.length === 0 && (
          <div className="flex flex-col items-center text-center py-12">
            <Typography
              variant="h3"
              weight="semibold"
              className="!text-slate-500"
            >
              No certificates yet
            </Typography>
            <Typography
              variant="small"
              className="text-textPrimary-grey p-2 mb-2"
            >
              Complete your first course to earn your first certificate!
            </Typography>
            <Button variant="filled" onClick={navigateToCourses}>
              Explore Courses
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificates;
